import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { getToken, setToken, clearToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Returning from Google OAuth: let AuthCallback exchange the session_id first.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    if (!getToken()) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      clearToken();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const googleLogin = async (session_id) => {
    const { data } = await api.post("/auth/google/session", { session_id });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setToken(data.token);
    if (data.email_verify_token) localStorage.setItem("sitepar_verify_token", data.email_verify_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearToken();
    setUser(false);
  };

  const setAccountType = async (type) => {
    const { data } = await api.post("/auth/account-type", { account_type: type });
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, googleLogin, register, logout, refresh, setUser, setAccountType }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
